/**
 * Public, browser-safe configuration for the practitioner signup flow.
 *
 * DEVELOPER NOTE
 * --------------
 * Only PUBLIC values belong here. The Supabase project URL and the Supabase
 * anon (publishable) key are designed to be shipped to browsers and are
 * protected by Row Level Security + Edge Function checks.
 *
 * NEVER add any of the following to this file or any frontend code:
 *   - Supabase service_role / secret key
 *   - Stripe secret key (sk_...) or restricted keys
 *   - Stripe webhook signing secret (whsec_...)
 *   - Stripe Price IDs mapping logic that the client could tamper with
 * Those live only in Supabase Edge Function secrets.
 *
 * Supply values through your build environment (e.g. Vite `VITE_*` vars).
 */

type EnvRecord = Record<string, string | undefined>

// Read build-time env without assuming a specific bundler's type definitions.
const env: EnvRecord =
  (
    import.meta as unknown as {
      env?: EnvRecord
    }
  ).env ?? {}

export const integrationConfig = {
  /** TODO(dev): set VITE_SUPABASE_URL — e.g. https://<project-ref>.supabase.co */
  supabaseUrl: (env.VITE_SUPABASE_URL ?? '').replace(/\/$/, ''),

  /** TODO(dev): set VITE_SUPABASE_ANON_KEY — the PUBLIC anon/publishable key only. */
  supabaseAnonKey: env.VITE_SUPABASE_ANON_KEY ?? '',

  /** Edge Function that creates the practitioner's Supabase Auth user. */
  createAccountFunction:
    env.VITE_SUPABASE_FN_CREATE_PRACTITIONER ?? 'create-practitioner-account',

  /** Edge Function that creates a Stripe Checkout Session for a plan. */
  checkoutFunction:
    env.VITE_SUPABASE_FN_PRACTITIONER_CHECKOUT ??
    'create-practitioner-checkout',

  /** Practitioner Terms of Service page. Override with VITE_TERMS_OF_SERVICE_URL if hosted elsewhere. */
  termsOfServiceUrl: env.VITE_TERMS_OF_SERVICE_URL ?? '/terms-of-service',
}

/** Keep in sync with Supabase Auth → Providers → Email → minimum password length. */
export const passwordMinLength = 8

export const practitionerRoutes = {
  landing: '/practitioners',
  join: '/practitioners/join',
  membership: '/practitioners/membership',
  welcome: '/practitioners/welcome',
} as const

/** Allowed hosted-checkout origin. The client refuses to redirect anywhere else. */
export const stripeCheckoutOrigin = 'https://checkout.stripe.com'

export function isBackendConfigured(): boolean {
  return Boolean(integrationConfig.supabaseUrl && integrationConfig.supabaseAnonKey)
}
